import React, { useEffect, useRef, useState } from 'react';
import { ImageFillIcon } from '../../../../assets/icons';
import styles from './ImageUpload.module.scss';
import Button from '../button/Button';

const getDisplayNameFromUrl = (url: string): string => {
    const raw = url.split('/').pop()?.split('?')[0] ?? '';
    const dot = raw.lastIndexOf('.');
    const base = dot === -1 ? raw : raw.slice(0, dot);
    const ext = dot === -1 ? '' : raw.slice(dot);
    const underscore = base.lastIndexOf('_');
    return (underscore === -1 ? base : base.slice(0, underscore)) + ext;
};

interface ImageUploadProps {
    onChange: (file: File | null) => void;
    onUserChange?: (file: File) => void;
    accept?: string;
    maxSizeMb?: number;
    error?: string;
    initialImageUrl?: string;
    initialFile?: File | null;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
    onChange,
    onUserChange,
    accept = '.jpg,.jpeg,.png,.webp',
    maxSizeMb = 5,
    error,
    initialImageUrl,
    initialFile,
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(initialImageUrl ?? null);
    const handledInitialFileRef = useRef<File | null>(null);

    useEffect(() => {
        if (initialImageUrl && !file) {
            setPreviewUrl(initialImageUrl);
        }
    }, [initialImageUrl]);

    useEffect(() => {
        if (initialFile && initialFile !== handledInitialFileRef.current) {
            handledInitialFileRef.current = initialFile;
            const url = URL.createObjectURL(initialFile);
            setFile(initialFile);
            setPreviewUrl(url);
            onChange(initialFile);
        }
    }, [initialFile]);
    const [isDragging, setIsDragging] = useState(false);
    const [progress, setProgress] = useState<number | null>(null);
    const [internalError, setInternalError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFile = (incoming: File) => {
        const validExtensions = accept.split(',').map(e => e.trim().toLowerCase());
        const ext = '.' + incoming.name.split('.').pop()?.toLowerCase();
        if (!validExtensions.includes(ext)) {
            setInternalError('Accepted formats: JPG, PNG, WEBP.');
            return;
        }
        if (incoming.size > maxSizeMb * 1024 * 1024) {
            setInternalError(`Image must not exceed ${maxSizeMb}MB.`);
            return;
        }
        setInternalError(null);

        if (previewUrl) URL.revokeObjectURL(previewUrl);

        setFile(incoming);
        setPreviewUrl(URL.createObjectURL(incoming));
        setProgress(0);

        let current = 0;
        const interval = setInterval(() => {
            current += 5;
            setProgress(current);
            if (current >= 100) {
                clearInterval(interval);
                onChange(incoming);
                onUserChange?.(incoming);
            }
        }, 100);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) handleFile(f);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f) handleFile(f);
    };

    const handleReupload = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (inputRef.current) inputRef.current.value = '';
        inputRef.current?.click();
    };

    const isLoading = progress !== null && progress < 100;
    const isLoaded = previewUrl !== null && !isLoading;

    return (
        <div
            className={`${styles.container} ${isDragging ? styles.dragging : ''} ${error ? styles.hasError : ''} ${isLoading ? styles.loading : ''}`}
            onClick={() => !isLoading && !isLoaded && inputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
        >
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                className={styles.hiddenInput}
                onChange={handleChange}
            />

            {isLoading ? (
                <div className={styles.loadingBlock}>
                    <div className={styles.iconWrapper}>
                        <ImageFillIcon />
                    </div>
                    <div className={styles.progressBlock}>
                        <span className={styles.title}>Uploading</span>
                        <div className={styles.progressBar}>
                            <div className={styles.progressFill} style={{ width: `${progress}%` }} />
                        </div>
                    </div>
                </div>
            ) : isLoaded ? (
                <>
                    <img src={previewUrl!} alt={file?.name ?? 'image'} className={styles.preview} />
                    {(file || initialImageUrl) && (
                        <span className={styles.fileName}>
                            {file?.name ?? getDisplayNameFromUrl(initialImageUrl!)}
                        </span>
                    )}
                    <Button variant="outline" onClick={handleReupload} text="Re-upload" />
                </>
            ) : (
                <>
                    <div className={styles.iconWrapper}>
                        <ImageFillIcon />
                    </div>
                    <div className={styles.textBlock}>
                        <span className={styles.title}>Add Image</span>
                        <span className={styles.subtitle}>Click here or drag and drop to upload</span>
                    </div>
                </>
            )}

            {(internalError || error) && <span className={styles.errorText}>{internalError ?? error}</span>}
        </div>
    );
};

export default ImageUpload;
